<template>
  <v-form ref="form" v-model="formValid">
    <v-container class="pt-8">
      <v-row>
        <v-col cols="4">
          <KeyField
            label="Survey Key"
            dense
            v-model="details.surveyKey"
            :disabled="disabled"
            :rules="rules.key"
            hint="Leave blank to auto-generate unique id"
            persistent-hint
          ></KeyField>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="4"
          ><v-text-field
            dense
            label="Name"
            class="required"
            v-model="details.name"
            :disabled="disabled"
            :rules="rules.name"
            :error-messages="$error.getValidationError(errors, 'Name')"
            @input="$error.clearValidationError(errors, 'Name')"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="8"
          ><v-textarea
            dense
            label="Description"
            v-model="details.description"
            :disabled="disabled"
            :rules="rules.description"
            :error-messages="$error.getValidationError(errors, 'Description')"
            @input="$error.clearValidationError(errors, 'Description')"
          ></v-textarea
        ></v-col>
      </v-row>
      <v-row>
        <v-col cols="4"
          ><v-text-field
            dense
            label="Completed Title"
            v-model="details.completedTitle"
            :disabled="disabled"
            :rules="rules.completedTitle"
            :error-messages="$error.getValidationError(errors, 'Completed Title')"
            @input="$error.clearValidationError(errors, 'Completed Title')"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="8"
          ><v-textarea
            dense
            label="Completed Description"
            v-model="details.completedDescription"
            :disabled="disabled"
            :rules="rules.completedDescription"
            :error-messages="$error.getValidationError(errors, 'Completed Description')"
            @input="$error.clearValidationError(errors, 'Completed Description')"
          ></v-textarea
        ></v-col>
      </v-row>
      <v-row>
        <v-col cols="3">
          <DateTimePickerField
            v-model="details.effectiveDate"
            :disabled="disabled"
            :label="$i18n.translate('Effective Date')"
            defaultTime="00:00"
            :rules="rules.getDateRules(details).effectiveDate"
            :error-messages="$error.getValidationError(errors, 'Effective Date')"
            @input="$error.clearValidationError(errors, 'Effective Date')"
          ></DateTimePickerField>
        </v-col>
        <v-col cols="3">
          <DateTimePickerField
            v-model="details.expirationDate"
            :disabled="disabled"
            :label="$i18n.translate('Expiration Date')"
            defaultTime="23:59"
            :rules="rules.getDateRules(details).expirationDate"
            :error-messages="$error.getValidationError(errors, 'Expiration Date')"
            @input="$error.clearValidationError(errors, 'Expiration Date')"
          ></DateTimePickerField>
        </v-col>
      </v-row>
    </v-container>
  </v-form>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "../../services/api.service";
import DateTimePickerField from "../fields/DateTimePickerField.vue";
import KeyField from "../fields/KeyField.vue";
import ValidationService from "@/gapp-components/services/validation.service.js";

export default {
  components: { KeyField, DateTimePickerField },
  name: "SurveyDetails",
  props: {
    value: Object,
    id: {
      default: 0
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    surveyQuestionTypes: [],
    errors: {},
    details: {},
    formValid: false
  }),
  methods: {
    onInput(obj) {
      this.$emit("input", obj);
    },
    fetchData() {
      return ApiService.get("/api/surveys/" + this.id).then(({ data }) => {
        this.$set(this, "details", data);
      });
    },
    validate() {
      if (!this.$refs.form) {
        return true;
      }
      const isValid = this.$refs.form.validate();
      this.formValid = isValid;
      this.$emit("update:valid", isValid);
      return isValid;
    },
    onSubmit() {
      if (!this.validate()) {
        return Promise.reject(new Error("Validation failed"));
      }
      return this.saveDetails();
    },
    saveDetails() {
      let postForm = Object.assign({}, this.details);

      postForm.program = this.$api.getSelfUrl("programs", this.selectedProgram);
      if (postForm.country) {
        postForm.country = { name: postForm.country.name };
      }

      let serviceCall = null;
      if (this.isNew) {
        serviceCall = this.$api.post("/api/surveys", postForm).then(({ data }) => {
          this.$emit("reload", data.id);
        });
      } else {
        delete postForm.program;
        delete postForm._links;
        delete postForm.updatedDate;
        delete postForm.createdDate;

        serviceCall = this.$api.patch("/api/surveys/" + this.id, postForm);
      }
      return serviceCall;
    }
  },
  mounted() {
    if (!this.isNew) {
      this.fetchData().then(() => {
        this.$emit("input", this.details);
        this.$nextTick(() => this.validate());
      });
    } else {
      this.$nextTick(() => this.validate());
    }
  },
  computed: {
    ...mapGetters(["selectedParticipant", "selectedProgram"]),
    isNew() {
      return this.id == "0" || this.id == 0;
    },
    rules() {
      return {
        key: [
          ValidationService.generateNotWhitespacesRule("Survey Key"),
          ...ValidationService.generateTextValidationRules("Survey Key", 100, false)
        ],
        name: [
          ValidationService.generateNotWhitespacesRule("Name"),
          ...ValidationService.generateTextValidationRules("Name")
        ],
        description: [
          ValidationService.generateNotWhitespacesRule("Description"),
          ...ValidationService.generateTextValidationRules("Description", 255, false)
        ],
        completedTitle: [
          ValidationService.generateNotWhitespacesRule("Completed Title"),
          ...ValidationService.generateTextValidationRules("Completed Title", 100, false)
        ],
        completedDescription: [
          ValidationService.generateNotWhitespacesRule("Completed Description"),
          ...ValidationService.generateTextValidationRules("Completed Description", 255, false)
        ],
        getDateRules: form => {
          const dateRules = ValidationService.generateDateRangeValidationRules(
            "Effective Date",
            "Expiration Date",
            () => form.effectiveDate,
            () => form.expirationDate,
            false
          );
          return {
            effectiveDate: [dateRules[0]],
            expirationDate: [dateRules[1]]
          };
        }
      };
    }
  },
  watch: {
    formValid(val) {
      this.$emit("update:valid", val);
    },
    details: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          if (this.$refs.form) {
            this.validate();
          }
        });
      }
    }
  }
};
</script>
